/**
 * Helper functions for building list table columns from ListViewSchema
 * 
 * Converts ListViewSchema column definitions to TanStack Table ColumnDef format.
 * Handles filter and sort conversion between Frappe format and React format.
 */

import React from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import type {
  ListViewSchema,
  ListViewVariantConfig,
  ListViewFilter,
  ListViewSortConfig,
} from "../listViewSchemas/types";
import type { FilterDescriptor, FilterOperator } from "@/lib/erp/filterTypes";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { OrderStatusBadge } from "@/features/orders/components/OrderStatusBadge";
import { ChannelBadge } from "@/features/channels/components/ChannelBadge";
import { cn } from "@/lib/utils";
import { formatDate, formatDateTime } from "@/lib/format";

/**
 * Map Frappe filter operator to FilterOperator enum
 */
const FRAPPE_TO_FILTER_OPERATOR: Record<string, FilterOperator> = {
  "=": "equals",
  "!=": "notEquals",
  ">": "greaterThan",
  "<": "lessThan",
  ">=": "greaterOrEqual",
  "<=": "lessOrEqual",
  "like": "like",
  "not like": "notLike",
  "in": "in",
  "not in": "notIn",
  "between": "between",
  "is": "is",
  "is not": "is", // Special handling needed for value
};

/**
 * Convert special filter values like "Today" to actual values
 */
function resolveFilterValue(value: any): any {
  if (value === "Today") {
    return new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  }
  // Add other special values as needed
  return value;
}

/**
 * Get active list view config for a given mode
 * 
 * @param schema - ListViewSchema
 * @param mode - "experience" or "system" (default: "experience")
 * @returns ListViewVariantConfig or null
 */
export function getActiveListViewConfig(
  schema: ListViewSchema,
  mode: "experience" | "system" = "experience"
): ListViewVariantConfig | null {
  if (mode === "experience" && schema.views.experience) {
    return schema.views.experience;
  }
  if (schema.views.system) {
    return schema.views.system;
  }
  return null;
}

/**
 * Convert ListViewFilter[] (Frappe format) to FilterDescriptor[]
 * 
 * @param filters - Frappe format filters: [fieldname, operator, value][]
 * @param doctype - DocType name for context
 * @returns FilterDescriptor[] for React components
 */
export function convertListViewFiltersToFilterDescriptors(
  filters: ListViewFilter[],
  _doctype: string
): FilterDescriptor[] {
  return filters.map(([fieldname, operator, value]) => {
    const resolvedValue = resolveFilterValue(value);
    const filterOperator = FRAPPE_TO_FILTER_OPERATOR[operator] || "equals";

    // Special handling for "is not" operator
    if (operator === "is not") {
      return {
        field: fieldname,
        operator: "is",
        value: resolvedValue === null || resolvedValue === "" ? "not set" : resolvedValue,
      };
    }

    return {
      field: fieldname,
      operator: filterOperator,
      value: resolvedValue,
    };
  });
}

/**
 * Convert ListViewSortConfig to TanStack Table SortingState
 * 
 * @param sortConfig - Sort configuration from schema
 * @returns TanStack Table sorting state
 */
export function convertListViewSortToTanStackSort(
  sortConfig?: ListViewSortConfig
): SortingState {
  if (!sortConfig) {
    return [];
  }

  return [
    {
      id: sortConfig.fieldname,
      desc: sortConfig.direction === "desc",
    },
  ];
}

/**
 * Render cell value based on column type
 */
function renderCellValue(value: any, type?: string, align?: string): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">-</span>;
  }

  switch (type) {
    case "Currency":
      return (
        <div className={cn(align === "right" && "text-right", "font-medium tabular-nums")}>
          {typeof value === "number"
            ? `\u0E3F${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
            : value}
        </div>
      );

    case "Date":
      return <div>{formatDate(value as string) || String(value)}</div>;

    case "Datetime":
      return <div>{formatDateTime(value as string) || String(value)}</div>;

    case "RelativeTime": {
      const date = new Date(value as string);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      const diffHr = Math.floor(diffMs / 3600000);
      const diffDay = Math.floor(diffMs / 86400000);
      let relative: string;
      if (diffMin < 1) relative = "just now";
      else if (diffMin < 60) relative = `${diffMin} min ago`;
      else if (diffHr < 24) relative = `${diffHr}h ago`;
      else if (diffDay < 7) relative = `${diffDay}d ago`;
      else relative = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
      return (
        <div className="text-muted-foreground text-xs" title={formatDateTime(value as string) || String(value)}>
          {relative}
        </div>
      );
    }

    case "Check":
      const checked = value === 1 || value === true || value === "1";
      return <StatusBadge color={checked ? "green" : "gray"}>{checked ? "Yes" : "No"}</StatusBadge>;

    case "Status": {
      // String workflow statuses (e.g. "To Deliver", "Completed")
      if (typeof value === "string" && isNaN(parseInt(value, 10))) {
        return <StatusBadge>{value}</StatusBadge>;
      }
      // Numeric docstatus fallback: 0 = Draft, 1 = Submitted, 2 = Cancelled
      const statusValue = typeof value === "number" ? value : parseInt(String(value), 10);
      const statusLabels: Record<number, string> = {
        0: "Draft",
        1: "Submitted",
        2: "Cancelled",
      };
      return <StatusBadge>{statusLabels[statusValue] || String(value)}</StatusBadge>;
    }

    case "Link":
      return <div>{String(value)}</div>;

    case "Tag":
      return <Badge variant="outline" className="text-[11px] px-1.5 py-0">{String(value)}</Badge>;

    case "Channel":
      return <ChannelBadge channel={String(value)} size={14} />;

    default:
      return <div>{String(value)}</div>;
  }
}

/**
 * Convert schema columns to DataTable column definitions.
 * This should be a thin mapping layer.
 * 
 * @param viewConfig - ListViewVariantConfig from schema
 * @param options - Optional configuration
 * @returns Array of TanStack Table ColumnDef
 */
export function buildColumnsFromListViewConfig<T = any>(
  viewConfig: ListViewVariantConfig,
  _options?: Record<string, unknown>
): ColumnDef<T, any>[] {
  return viewConfig.columns.map((col) => {
    const align = col.align || "left";
    const type = col.type || "Data";
    const sortable = col.sortable !== false; // Default to true

    return {
      id: col.fieldname,
      accessorKey: col.fieldname,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label={col.label || col.fieldname} />
      ),
      cell: ({ row }) => {
        const value = row.getValue(col.fieldname);

        // OrderStatus needs full row data for per_delivered + marketplace status + payment
        if (type === "OrderStatus") {
          const orig = row.original as Record<string, unknown>;
          const mktStatus = (orig?.shopee_order_status || orig?.lazada_order_status) as string | undefined;
          const grandTotal = Number(orig?.grand_total ?? 0);
          const advancePaid = Number(orig?.advance_paid ?? 0);
          const isPaid = grandTotal > 0 && advancePaid >= grandTotal;
          return (
            <div className={cn(align === "center" && "text-center", col.hideOnMobile && "hidden md:block")}>
              <OrderStatusBadge
                status={String(value ?? "")}
                perDelivered={orig?.per_delivered as number | undefined}
                marketplaceStatus={mktStatus}
                paymentVerified={isPaid}
              />
            </div>
          );
        }

        return (
          <div
            className={cn(
              align === "right" && "text-right",
              align === "center" && "text-center",
              col.hideOnMobile && "hidden md:block"
            )}
          >
            {renderCellValue(value, type, align)}
          </div>
        );
      },
      enableSorting: sortable,
      enableColumnFilter: false, // Column filtering handled via FilterModal
      enableResizing: true,
      size: col.width && typeof col.width === "number" ? col.width : 150,
      minSize: 50,
      maxSize: 500,
      meta: {
        label: col.label || col.fieldname,
        align,
        width: col.width || "auto",
        type,
        hideOnMobile: col.hideOnMobile || false,
      },
    } as ColumnDef<T, any>;
  });
}

